import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { MatOption } from "@angular/material/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { ICellRendererParams } from "ag-grid-community";
import { DropdownComponent } from "../../dropdown/dropdown.component";
import { BehaviorSubject } from "rxjs";

@UntilDestroy()
@Component({
  selector: "dropdown-cell-editor",
  styleUrls: ["./dropdown-cell-editor.component.scss"],
  standalone: true,
  imports: [DropdownComponent, CommonModule],
  templateUrl: "./dropdown-cell-editor.component.html",
})
export class DropdownCellEditor implements ICellRendererAngularComp {
  refresh(params: ICellRendererParams<any, any>): boolean {
    return true;
  }
  private params!: ICellRendererParams;
  public value!: any;
  dropdownOptions = [] as any;
  showDash = false;
  readonlyOnEdit = false;
  hasId: boolean = false;
  required: boolean = false;
  changeFn: any;
  loaded = false;
  disabled!: boolean;
  readonlyDisplayValue?: string;
  hint?: string;
  selectDefault: boolean = true;
  showRequired: BehaviorSubject<boolean> | undefined;
  isRequired: boolean = false;
  requiredText: string = "*";
  isOptionSelected: boolean = false;
  isTouched: boolean = false;

  getWarningClass() {
    return {
      "text-danger": this.isRequired,
      "block-display": this.isRequired,
    };
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;

    this.value = this.params.value;
    this.dropdownOptions =
      this.params.colDef?.cellRendererParams?.options ||
      this.params.colDef?.cellRendererParams?.optionsGetter(this.params.data);
    this.determineReadonly();
    this.changeFn = this.params.colDef?.cellRendererParams?.onChange;
    this.required = this.params.colDef?.cellRendererParams?.required;
    this.requiredText = this.params.colDef?.cellRendererParams?.requiredText;
    this.showRequired = this.params.colDef?.cellRendererParams?.showRequired;
    this.showRequired?.pipe(untilDestroyed(this)).subscribe((data) => {
      this.isRequired = data;
    });
    this.isRequired = false;
    this.loaded = true;
    this.params.colDef?.cellRendererParams?.disabled$
      ?.pipe(untilDestroyed(this))
      .subscribe((disabled: boolean) => {
        this.disabled = disabled;
      });
    this.hint =
      this.params.colDef?.cellRendererParams?.hint ||
      this.params.colDef?.cellRendererParams?.hintGetter?.(this.params.data);
  }

  private determineReadonly(): void {
    this.hasId =
      !!this.params.data?.[this.params.colDef?.cellRendererParams?.idField];
    this.readonlyOnEdit =
      this.params.colDef?.cellRendererParams?.readonlyOnEdit;
    if (this.readonlyOnEdit && this.hasId) {
      this.readonlyDisplayValue =
        this.dropdownOptions.find((option: any) => option.id === this.value)
          ?.name || this.value;
    }
  }

  onOptionSelected(change: MatOption) {
    if (change == null || change.value == null) {
      this.value = null;
      this.isOptionSelected = false;
      this.changeFn(null, this.params.rowIndex, this.params.colDef?.field);
    } else {
      this.isOptionSelected = true;
      this.value = change.value;
      this.changeFn(
        change.value,
        this.params.rowIndex,
        this.params.colDef?.field
      );
    }
  }

  /* Component Editor Lifecycle methods */
  // the final value to send to the grid, on completion of editing
  getValue() {
    // this simple editor doubles any value entered into the input
    return this.value;
  }

  // Gets called once before editing starts, to give editor a chance to
  // cancel the editing before it even starts.
  isCancelBeforeStart() {
    return false;
  }

  // Gets called once when editing is finished (eg if Enter is pressed).
  // If you return true, then the result of the edit will be ignored.
  isCancelAfterEnd() {
    // our editor will reject any value greater than 1000
    return this.value > 1000;
  }

  onFocus(): void {
    this.isTouched = true;
  }
}
